<template>
  <div class="grow flex flex-col">
    <p class="mb-4">Media Library</p>
    <!-- <img src="https://assets.marketingassistant.com/images/illustrations/image.svg" />
    <p class="mt-4">This feature is coming soon</p> -->

    <AppLoading
      v-if="isLoading || isLoadingFiles"
      title-class="hidden"
      wrapper-class="h-32"
    />
    <AppNoContent
      v-else-if="isError"
      title-class="hidden"
      wrapper-class="h-32"
      :button-text="$t('auth.retry')"
      :message="$t('imageLibraryLoadError')"
      :loading="isRefetching"
      @button-action="refetch()"
    />
    <AppNoContent
      v-else-if="!data?.length && !userFiles?.length"
      :title="$t('noImagesInLibrary')"
      :description="$t('imageLibraryEmptyStateText')"
      :button-text="$t('goToImgLibrary')"
      @button-action="goToImageLibrary"
    />
    <ContentEditMediaPreview
      v-else-if="activeImageUrl"
      :url="activeImageUrl"
      :class="small ? 'px-6 @sm:px-10' : ''"
      class="pb-6"
      :media-type="activeMediaType"
      @cancel="resetActiveMedia"
      @accept="handleAttachmentReady"
    />
    <div v-else>
      <!-- temp hidden -->
      <div v-for="(library, group) in groupedImages" :key="group" class="hidden">
        <p class="text-sm ma-text-secondary mb-1 font-medium">
          <i class="pi pi-tag" />
          {{ group }}
        </p>
        <div class="grid gap-1" :class="small ? 'grid-cols-3' : 'grid-cols-2'">
          <div
            v-for="image in library"
            :key="image.link"
            class="overflow-hidden relative group w-full aspect-square"
            :class="images[image.link] ? 'opacity-50 cursor-not-allowed' : ''"
            role="button"
            @click.stop="activeImageUrl = images[image.link] ? '' : image.link"
          >
            <img
              :src="image.link"
              :alt="image.label"
              class="hover:scale-110 duration-300 group-hover:brightness-50 w-full aspect-square bg-surface-200 dark:bg-surface-700 object-cover"
            />
            <p
              class="absolute inset-x-0 bottom-0 p-1 text-sm text-surface-0 opacity-0 group-hover:opacity-100 pointer-events-none truncate"
            >
              {{ image.label }}
            </p>
          </div>
        </div>
      </div>
      <!-- user images -->
      <div v-if="userFiles.length">
        <p class="text-sm ma-text-secondary mb-1 font-medium">
          <i class="pi pi-user" />
          {{ $t("wordsAccount") }}
        </p>
        <div class="grid gap-1" :class="small ? 'grid-cols-3' : 'grid-cols-2'">
          <div
            v-for="image in userFiles"
            :key="image.link"
            class="overflow-hidden relative group"
            :class="{
              'opacity-50 cursor-not-allowed':
                (image.isVideo ? props.videos?.[image.link] : props.images[image.link]) ||
                (image.isVideo
                  ? props.disableVideoSelection
                  : props.disableImageSelection),
            }"
            role="button"
            @click.stop="selectMedia(image)"
          >
            <video
              v-if="image.isVideo"
              :src="image.link"
              class="hover:scale-110 duration-300 group-hover:brightness-50 w-full aspect-square bg-surface-200 dark:bg-surface-700 object-cover"
              muted
            ></video>
            <img
              v-else
              :src="image.link"
              :alt="image.label"
              class="hover:scale-110 duration-300 group-hover:brightness-50 w-full aspect-square bg-surface-200 dark:bg-surface-700 object-cover"
            />
            <div
              class="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors"
            />
            <i
              v-if="image.isVideo"
              class="pi pi-play-circle absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl text-white opacity-70"
            />
            <p
              class="absolute bottom-0 p-1 text-sm text-surface-0 opacity-0 group-hover:opacity-100 pointer-events-none z-10"
            >
              {{ image.label }}
            </p>
          </div>
        </div>
      </div>
      <AppNoContent
        v-else-if="!userFiles?.length"
        :title="$t('noImagesInLibrary')"
        :description="$t('imageLibraryEmptyStateText')"
        :button-text="$t('goToImgLibrary')"
        @button-action="goToImageLibrary"
      />
    </div>

    <!-- <pre>{{ groupedImages }}</pre> -->
  </div>
</template>

<script setup lang="ts">
import type { LibraryImage } from "@/types/content";
import ContentEditMediaPreview from "./ContentEditMediaPreview.vue";
import AppLoading from "@/components/shared/AppLoading.vue";
import AppNoContent from "@/components/shared/AppNoContent.vue";

const props = defineProps<{
  images: Record<string, boolean>;
  small?: boolean;
  videos?: Record<string, boolean>;
  disableImageSelection?: boolean;
  disableVideoSelection?: boolean;
}>();

const emit = defineEmits<{
  done: [string];
  imageSelected: [string];
  videoSelected: [string];
}>();

const { data, isLoading, isError, refetch, isRefetching } = useImagesFromLibrary();
const { data: files, isLoading: isLoadingFiles } = useFilesListRequest();
const activeImageUrl = ref("");
const activeMediaType = ref<"image" | "video">("image");
const router = useRouter();

const groupedImages = computed(() => {
  const images = data.value;
  const grouped: Record<string, LibraryImage[]> = {};
  if (!images?.length) return grouped;
  images.forEach(image => {
    if (!grouped[image.category]) {
      grouped[image.category] = [];
    }
    grouped[image.category].push(image);
  });
  return grouped;
});

const userFiles = computed(() => {
  if (!files.value) return [];
  return files.value
    .filter(file => {
      return (
        file.contentType.startsWith("image/") || file.contentType.startsWith("video/")
      );
    })
    .map(file => ({
      ...file,
      link: enforceFilesDomain(file.path) || "",
      label: file.name,
      isVideo: file.contentType.startsWith("video/"),
    }));
});

function selectMedia(media: { link: string; isVideo?: boolean }) {
  // Check if media type selection is disabled
  const isDisabled = media.isVideo
    ? props.disableVideoSelection
    : props.disableImageSelection;

  if (isDisabled) return;

  // Check if already selected based on media type
  const isSelected = media.isVideo
    ? props.videos?.[media.link]
    : props.images[media.link];
  if (isSelected) return;

  activeImageUrl.value = media.link;
  activeMediaType.value = media.isVideo ? "video" : "image";
}

function resetActiveMedia() {
  activeImageUrl.value = "";
  activeMediaType.value = "image";
}

function handleAttachmentReady() {
  if (!activeImageUrl.value) return;

  // Emit the correct event based on media type
  if (activeMediaType.value === "video") {
    emit("videoSelected", activeImageUrl.value);
  } else {
    emit("imageSelected", activeImageUrl.value);
  }

  resetActiveMedia();
}

function goToImageLibrary() {
  router.push({
    path: "/my-assistant",
    query: {
      currentTab: "files",
    },
  });
}
</script>
